"use client"

import { useState, useEffect, useCallback } from "react"
import { v4 as uuidv4 } from "uuid"
import { Item } from "@/types/purchase"
import { logError } from "@/lib/sentry"

import { CustomerTemplate } from "@/components/CustomerTemplateManager";

export function useCustomerTemplates(userId: string | null | undefined, pseudoUserId?: string) {
  const [customerTemplates, setCustomerTemplates] = useState<CustomerTemplate[]>([])
  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // ---- Helper function to get sessionStorage keys ----
  const getSessionKeys = () => {
    if (userId) {
      return {
        templatesKey: `customer-templates-${userId}`,
        activeIdKey: `active-customer-${userId}`,
      };
    } else if (pseudoUserId) {
      return {
        templatesKey: `customer-templates-${pseudoUserId}`,
        activeIdKey: `active-customer-${pseudoUserId}`,
      };
    } else {
      // pseudoUserId가 없는 비회원 시나리오에 대한 폴백 (이론적으로는 발생하지 않아야 함)
      // AuthContext에서 pseudoUserId는 항상 존재하도록 관리됨
      console.warn("[useCustomerTemplates] pseudoUserId가 없어 guest 키 사용 (예상치 못한 상황)")
      return {
        templatesKey: "customer-templates-guest",
        activeIdKey: "active-customer-guest",
      };
    }
  };

  // Load saved templates from sessionStorage when component mounts
  useEffect(() => {
    setIsLoading(true);
    
    const { templatesKey, activeIdKey } = getSessionKeys();
    console.log(`[useCustomerTemplates] 💾 로드 시도: templatesKey=${templatesKey}, activeIdKey=${activeIdKey}`);

    // 회원가입 직후 문제 방지: userId가 변경된 직후에 호출된 경우 로그 추가
    const isUserIdChange = userId !== null && userId !== undefined && 
      sessionStorage.getItem(`previous-user-id-check`) !== userId;
    
    if (isUserIdChange) {
      console.log(`[useCustomerTemplates] ⚠️ userId 변경 감지: 이전=없음, 현재=${userId}`);
      sessionStorage.setItem(`previous-user-id-check`, userId);
    }

    const savedTemplates = sessionStorage.getItem(templatesKey);
    const savedActiveId = sessionStorage.getItem(activeIdKey);

    if (savedTemplates) {
      try {
        const parsed = JSON.parse(savedTemplates) as CustomerTemplate[];
        if (parsed.length > 0) {
          // 회원가입 직후 문제 방지: 변환된 데이터에 빈 아이템 배열로만 이루어진 고객 데이터가 있는지 확인
          const hasOnlyEmptyItemCustomers = parsed.every(c => !c.items || c.items.length === 0);
          const isDefaultCustomerOnly = parsed.length === 1 && 
            (parsed[0].nickname === "고객 닉네임" || parsed[0].nickname === "");
          
          if (isUserIdChange && hasOnlyEmptyItemCustomers && isDefaultCustomerOnly) {
            console.log(`[useCustomerTemplates] ⚠️ 회원가입 직후 빈 고객 데이터 감지. 기본 고객으로 초기화.`);
            addDefaultCustomer();
          } else {
            setCustomerTemplates(parsed);
            if (savedActiveId && parsed.some((t: CustomerTemplate) => t.id === savedActiveId)) {
              setActiveCustomerId(savedActiveId);
            } else {
              setActiveCustomerId(parsed[0].id);
            }
          }
        } else {
          // 저장된 데이터가 빈 배열이면 기본 고객 추가
          addDefaultCustomer();
        }
      } catch (e) {
        logError("Error parsing saved customer templates from", templatesKey, e);
        addDefaultCustomer();
      }
    } else {
      // 저장된 템플릿이 없으면 기본 고객 생성
      console.log("[useCustomerTemplates] 저장된 템플릿 없음, 기본 고객 생성.");
      addDefaultCustomer();
    }
    
    setIsLoading(false);
  }, [userId, pseudoUserId]); // pseudoUserId도 의존성 배열에 추가

  // Save templates to sessionStorage whenever they change
  useEffect(() => {
    const { templatesKey, activeIdKey } = getSessionKeys();
    // customerTemplates가 초기화(addDefaultCustomer 호출 등)되기 전에는 저장하지 않도록 조건 추가
    if (customerTemplates && customerTemplates.length > 0) {
      console.log(`[useCustomerTemplates] 💾 저장 시도: templatesKey=${templatesKey}, activeIdKey=${activeIdKey}`, customerTemplates);
      sessionStorage.setItem(templatesKey, JSON.stringify(customerTemplates));
      if (activeCustomerId) {
        sessionStorage.setItem(activeIdKey, activeCustomerId);
      }
    } else if (customerTemplates && customerTemplates.length === 0) {
        // 고객이 모두 삭제되어 customerTemplates가 빈 배열이 된 경우, 해당 키의 항목을 삭제하거나 빈 배열로 저장할 수 있습니다.
        // 현재는 빈 배열일 경우 아무것도 저장하지 않으므로, 이전 데이터가 남아있을 수 있습니다.
        // 명시적으로 삭제하려면:
        // sessionStorage.removeItem(templatesKey);
        // sessionStorage.removeItem(activeIdKey);
        // console.log(`[useCustomerTemplates] 🗑️ 고객 목록이 비어 ${templatesKey} 및 ${activeIdKey}를 sessionStorage에서 삭제 (또는 빈 배열 저장).`);
    }
  }, [customerTemplates, activeCustomerId, userId, pseudoUserId]); // pseudoUserId도 의존성 배열에 추가

  // Add a new customer
  const addCustomer = useCallback((nickname: string) => {
    console.log("useCustomerTemplates.addCustomer called with:", nickname);

    const newCustomer: CustomerTemplate = {
      id: uuidv4(),
      nickname,
      template: "",
      items: [],
      is_keep: false, // Initialize is_keep
      status: "미지정", // Initialize status
      memo: "",
      totalPrice: 0,
      createdAt: new Date().toISOString(),
      paymentMethod: null,
      order_date: new Date().toISOString().slice(0, 10)
    }

    console.log("Created new customer:", newCustomer);

    setCustomerTemplates((prev) => {
      console.log("Previous customerTemplates:", prev);
      const newTemplates = [...prev, newCustomer];
      console.log("New customerTemplates:", newTemplates);
      return newTemplates;
    });

    setActiveCustomerId(newCustomer.id);
    console.log("Set activeCustomerId to:", newCustomer.id);

    return newCustomer.id;
  }, []);

  // Remove a customer
  const removeCustomer = useCallback((customerId: string) => {
    setCustomerTemplates((prev) => {
      const filtered = prev.filter((c) => c.id !== customerId);
      if (filtered.length === 0) {
        // 고객이 모두 삭제되면 기본 고객을 생성하여 반환
        // setActiveCustomerId는 이 함수를 호출한 Home.tsx의 useEffect에서 처리
        return [createDefaultCustomer()];
      }
      return filtered;
    });
    // setActiveCustomerId 로직은 Home.tsx의 useEffect에서 customerTemplates 변경 감지 후 처리
  }, []);

  // Update a customer's template
  const updateTemplate = useCallback((customerId: string, template: string) => {
    // 고객별 템플릿 업데이트는 유지하되, Home.tsx에서 호출할 때 처리 방식 변경
    setCustomerTemplates((prev) => prev.map((c) => (c.id === customerId ? { ...c, template } : c)))
  }, []);

  // Get the active customer's template
  const getActiveTemplate = () => {
    // 고객별 템플릿 대신 항상 빈 문자열 반환
    return "";
  }

  // 고객별 상품 목록 관리 함수 추가
  const updateCustomerItems = useCallback((customerId: string, items: Item[]) => {
    console.log(`[useCustomerTemplates] 🛎️ updateCustomerItems 호출됨: customerId=${customerId}, items개수=${items.length}`);

    const validItems = items.filter(item =>
      item &&
      item.name &&
      item.name.trim() !== "" &&
      !isNaN(Number(item.price)) &&
      !isNaN(Number(item.quantity)) &&
      Number(item.quantity) > 0
    );

    const totalPrice = validItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    setCustomerTemplates((prev) => {
      console.log(`[useCustomerTemplates] 🧑‍🍳 이전 customerTemplates (길이: ${prev.length}):`, prev);
      const newTemplates = prev.map((c) =>
        c.id === customerId ? { ...c, items: validItems, totalPrice } : c
      );
      console.log(`[useCustomerTemplates] ✨ 새 customerTemplates (길이: ${newTemplates.length}):`, newTemplates);

      return newTemplates;
    });
  }, []);

  // 현재 활성화된 고객의 상품 목록 가져오기
  const getActiveCustomerItems = () => {
    if (!activeCustomerId) return []
    const customer = customerTemplates.find((c) => c.id === activeCustomerId)
    return customer?.items || []
  }

  // 고객 닉네임 업데이트 함수 수정
  const updateCustomerNickname = useCallback((customerId: string, newNickname: string) => {
    setCustomerTemplates(prev =>
      prev.map(customer =>
        customer.id === customerId
          ? { ...customer, nickname: newNickname }
          : customer
      )
    );
  }, []);

  // 고객이 없을 때 자동으로 디폴트 고객 추가하는 useEffect 추가
  useEffect(() => {
    if (customerTemplates.length === 0) {
      addDefaultCustomer();
    }
  }, [customerTemplates.length]);

  // 디폴트 고객 객체 생성 함수 수정
  const createDefaultCustomer = (): CustomerTemplate => {
    return {
      id: uuidv4(),
      nickname: "고객 닉네임",
      template: "",
      items: [],
      is_keep: false, // Initialize is_keep
      status: "미지정", // Initialize status
      memo: "",
      totalPrice: 0,
      createdAt: new Date().toISOString(),
      paymentMethod: null,
      order_date: new Date().toISOString().slice(0, 10)
    };
  };

  // 기본 고객 추가 함수 수정
  const addDefaultCustomer = () => {
    const defaultCustomer = createDefaultCustomer();
    setCustomerTemplates([defaultCustomer]);
    setActiveCustomerId(defaultCustomer.id);
    return defaultCustomer.id;
  };

  return {
    customerTemplates,
    activeCustomerId,
    setActiveCustomerId,
    addCustomer,
    removeCustomer,
    updateTemplate,
    getActiveTemplate,
    updateCustomerItems,
    getActiveCustomerItems,
    updateCustomerNickname,
    isLoading,
  }
} 
